import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar, Toolbar, Button, IconButton, Grid, useTheme, useMediaQuery,
  CssBaseline, createTheme, ThemeProvider, Menu, MenuItem, Drawer, List, ListItem, Divider,
  InputBase, Paper, Switch
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { Brightness4, Brightness7, ArrowDropDown } from '@mui/icons-material';
import logo from '../images/lg.png';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import '../css/style.css';  // Import file CSS
import '../i18n';

// Styled components
const Logo = styled('img')(({ theme }) => ({
  width: '200px',
  height: 'auto',
  [theme.breakpoints.down('sm')]: {
    width: '120px',
  },
}));

const NavLinks = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'nowrap',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'inline-flex',
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  justifyContent: 'flex-end',
}));

const Search = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '2px 4px',
  marginLeft: theme.spacing(2),
  borderRadius: '4px',
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    color: '#4caf50',
    '&.Mui-checked': {
      color: '#4caf50',
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#4caf50',
    },
  },
}));

const SwitchWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  color: 'black',
});

const Header = () => {
  const { t, i18n } = useTranslation(); // Use translation hook
  const [mode, setMode] = useState('light');
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null); // Manages the dropdown state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleThemeToggle = () => {
    setMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLoginClick = () => {
    window.open('/login', '_blank');
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleDropdownOpen = (event) => {
    setDropdownOpen(prev => (prev ? null : event.currentTarget));
  };

  const handleLanguageChange = () => {
    const newLang = i18n.language === 'en' ? 'id' : 'en';
    i18n.changeLanguage(newLang);
  };

  const customTheme = createTheme({
    palette: {
      mode: mode,
      background: {
        default: mode === 'light' ? '#fff' : '#121212',
      },
    },
  });

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <AppBar position="fixed" color={mode === 'light' ? 'default' : 'inherit'} elevation={4}>
        <Toolbar disableGutters>
          <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
            {/* Logo */}
            <Grid item>
              <Link to="/">
                <Logo src={logo} alt="Logo" />
              </Link>
            </Grid>

            {/* Navigation Links */}
            <NavLinks item>
              <Button component={Link} to="/" color="inherit">{t('Home')}</Button>
              <Button color="inherit" onClick={handleDropdownOpen} endIcon={<ArrowDropDown />}>
                {t('Zodiak')}
              </Button>
              <Menu
                anchorEl={dropdownOpen}
                open={Boolean(dropdownOpen)}
                onClose={() => setDropdownOpen(null)}
                PaperProps={{
                  sx: {
                    width: '200px',
                    maxWidth: 'none',
                    borderRadius: '4px',
                    boxShadow: theme.shadows[3],
                  },
                }}
              >
                <MenuItem component={Link} to="/zodiac-love" onClick={handleClose}>{t('Zodiac Love')}</MenuItem>
                <MenuItem component={Link} to="/zodiac-personality" onClick={handleClose}>{t('Zodiac Personality')}</MenuItem>
                <MenuItem component={Link} to="/Zodiak" onClick={handleClose}>{t('Zodiac Prediction')}</MenuItem>
              </Menu>
              <Button component={Link} to="/zodiac-health-and-wellness" color="inherit">{t('Tarot Reading')}</Button>
              <Button component={Link} to="/contacts" color="inherit">{t('Contacts')}</Button>
              <IconButton color="inherit" onClick={handleThemeToggle}>
                {mode === 'light' ? <Brightness4 /> : <Brightness7 />}
              </IconButton>
              <SwitchWrapper>
                <div>{i18n.language === 'en' ? 'ID' : 'EN'}</div>
                <StyledSwitch
                  checked={i18n.language === 'id'}
                  onChange={handleLanguageChange}
                  inputProps={{ 'aria-label': 'language switcher' }}
                />
                <div>{i18n.language === 'en' ? 'EN' : 'ID'}</div>
              </SwitchWrapper>
            </NavLinks>

            {/* Login Button */}
            <Grid item>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingRight: '20px' }}>
                {!isMobile && (
                  <Button 
                    onClick={handleLoginClick} 
                    variant="contained" 
                    color="warning" 
                    sx={{ 
                      fontSize: '14px',
                      padding: '6px 12px',
                      marginLeft: '10px'
                    }}
                  >
                    {t('Login')}
                  </Button>
                )}
                <MobileMenuButton edge="end" color="inherit" onClick={handleMobileMenuOpen}>
                  <MenuIcon />
                </MobileMenuButton>
                <Search>
                  <InputBase
                    placeholder={t('Search')}
                    startAdornment={<SearchIcon />}
                    sx={{ flex: 1 }}
                  />
                </Search>
              </div>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            width: '250px',
          },
        }}
      >
        <DrawerHeader>
          <IconButton onClick={handleMobileMenuClose}>
            <MenuIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          <ListItem button component={Link} to="/" onClick={handleMobileMenuClose}>{t('Home')}</ListItem>
          <ListItem button onClick={handleDropdownOpen}>{t('Zodiak')}</ListItem>
          <Menu
            anchorEl={dropdownOpen}
            open={Boolean(dropdownOpen)}
            onClose={() => setDropdownOpen(null)}
            PaperProps={{
              sx: {
                width: '100%',
                maxWidth: 'none',
                borderRadius: '4px',
                boxShadow: theme.shadows[3],
              },
            }}
          >
            <MenuItem component={Link} to="/zodiac-love" onClick={handleClose}>{t('Zodiac Love')}</MenuItem>
            <MenuItem component={Link} to="/zodiac-personality" onClick={handleClose}>{t('Zodiac Personality')}</MenuItem>
            <MenuItem component={Link} to="/Zodiak" onClick={handleClose}>{t('Zodiac Prediction')}</MenuItem>
          </Menu>
          <ListItem button component={Link} to="/zodiac-health-and-wellness" onClick={handleMobileMenuClose}>{t('Tarot Reading')}</ListItem>
          <ListItem button component={Link} to="/contacts" onClick={handleMobileMenuClose}>{t('Contacts')}</ListItem>
          <Divider />
          <ListItem button onClick={handleThemeToggle}>
            {mode === 'light' ? t('Dark Mode') : t('Light Mode')}
          </ListItem>
          <ListItem button onClick={handleLoginClick}>{t('Login')}</ListItem>
          <ListItem button onClick={handleLanguageChange}>
            {i18n.language === 'en' ? t('Switch to Indonesian') : t('Switch to English')}
          </ListItem>
        </List>
      </Drawer>

      <Toolbar />
    </ThemeProvider>
  );
};

export default Header;
